import type { ApiClient } from "@/modules/api/apiClient.ts";
import type * as billingCardsQuery from "@/modules/billing/billingCardsQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import {
    failureToMessage,
    persistentDataRequest,
    pollForSuccess,
    type PollForSuccessResult,
    type PollOptions,
} from "@/modules/billing/payment/paymentUtils.ts";
import { SimpleLocalizedError, type Localizable } from "@/modules/l10n/getTransText.ts";
import { msg } from "@lingui/core/macro";
import type { PersistentDataResponse } from "@warrenio/api-spec/spec.oats.gen";
import type { EmptyObject, LiteralUnion } from "type-fest";

export interface OmiseChargeData {
    object: string;
    status: "pending" | "successful" | "failed";
    failure_code?: LiteralUnion<"payment_cancelled", string>;
    failure_message?: string;
}

/** @public */
export class OmiseBackendError extends SimpleLocalizedError {
    name = "OmiseError";
    constructor(
        public readonly code: "payment_cancelled" | "other",
        localeMessage: Localizable,
    ) {
        super(localeMessage);
    }
}

function parseOmisePersistentData(response: PersistentDataResponse) {
    const data = JSON.parse(response.data) as OmiseChargeData | EmptyObject;
    if (!("object" in data)) {
        return null;
    }

    switch (data.status) {
        case "pending":
            return null;
        case "successful":
            return data;
        default:
            throw new OmiseBackendError(
                data.failure_code === "payment_cancelled" ? "payment_cancelled" : "other",
                failureToMessage(data),
            );
    }
}

export async function checkForOmisePersistentData({
    account,
    apiClient,
    signal,
    persistent_data_ref,
}: {
    account: EBillingAccount;
    apiClient: ApiClient;
    signal: AbortSignal;
    persistent_data_ref: string;
}): Promise<boolean> {
    return (
        parseOmisePersistentData(await persistentDataRequest(apiClient, account.id, persistent_data_ref, signal)) !=
        null
    );
}

class OmiseCardVerificationError extends OmiseBackendError {
    name = "OmiseCardVerificationError";
    constructor(public readonly cardId: number) {
        super("other", msg`Card verification failed`);
    }
}

class OmiseCardAddTimeoutError extends OmiseBackendError {
    name = "OmiseCardAddTimeoutError";
    constructor() {
        super(
            "other",
            msg`Response seems to be taking longer than expected. Wait a bit and refresh the page to see changes reflected. The result might be either successful or unsuccessful.`,
        );
    }
}

type PollOmiseCardValidationResult = Exclude<PollForSuccessResult, "WAIT_RESPONSE" | "CLOSE_MANUALLY">;

export async function pollForOmiseCardValidationSuccess(
    options: Omit<PollOptions, "check">,
    cardId: number,
    getCards: () => Promise<billingCardsQuery.Response>,
): Promise<PollOmiseCardValidationResult> {
    return (await pollForSuccess({
        check: async () => {
            const cards = await getCards();
            const card = cards.find((c) => c.id === cardId);
            if (card?.is_verified === false && card?.is_verification_pending === false) {
                throw new OmiseCardVerificationError(cardId);
            }
            return card?.is_verified === true;
        },
        timeoutError: new OmiseCardAddTimeoutError(),
        pollInterval: 6000,
        maxPollCount: 50,
        ...options,
    })) as PollOmiseCardValidationResult;
}
